import * as Sentry from '@sentry/react';

import { getIsPageTearingDown } from '~/utils/pageTeardown';
import { isHardResourceFailure } from '~/utils/resourceTimingDiagnostics';
import {
  recordSuppressedError,
  SuppressedErrorReason,
} from '~/utils/suppressedErrorTelemetry';

const RELOAD_MARKER_KEY = 'chunkLoadRecoveryReloadedAt';
const RELOAD_COOLDOWN_MS = 30_000;

function hasRecentlyReloaded() {
  const marker = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
  if (!marker) return false;

  const elapsed = Date.now() - Number(marker);
  return (
    Number.isFinite(elapsed) && elapsed >= 0 && elapsed < RELOAD_COOLDOWN_MS
  );
}

function extractFailedCssHref(message: string) {
  const match = /Unable to preload CSS for (\S+)/.exec(message);
  return match?.[1] ?? null;
}

// The browser only ever reports "this <link> failed to load" — it never says why, even
// when the real cause is a nested `@import` on a different origin (see UI-79X: Syncfusion's
// material.css @imports Google Fonts, and that's what actually fails for affected users).
// Resource Timing separately records every fetch a stylesheet's @imports trigger, tagged
// `initiatorType: "css"`, including failed ones — this is the only way to recover the
// real culprit URL for Sentry. Narrowed to failures starting after this specific
// stylesheet's own load, since that's what the @import it triggered would look like.
function findFailedCssSubresource(cssHref: string | null) {
  if (!cssHref) return null;

  const entries = performance.getEntriesByType(
    'resource',
  ) as PerformanceResourceTiming[];
  const cssEntry = entries.find((entry) => entry.name === cssHref);
  const searchStart = cssEntry?.startTime ?? 0;

  return (
    entries.find(
      (entry) =>
        entry.initiatorType === 'css' &&
        entry.name !== cssHref &&
        entry.startTime >= searchStart &&
        isHardResourceFailure(entry),
    ) ?? null
  );
}

// Runs for every preload failure, including ones we're about to auto-reload past —
// reload discards the tab before Sentry could use it anyway, but this stays cheap and
// unconditional so we never have to remember to call it from just the crash paths.
function attachCssPreloadDiagnostics(module: string) {
  const culprit = findFailedCssSubresource(extractFailedCssHref(module));
  if (!culprit) return;

  // Persists on the current scope, so it rides along with whatever Sentry captures
  // next — the uncaught exception this same preload failure is about to throw into
  // React.lazy's Suspense, once it propagates past this handler.
  Sentry.setContext('cssPreloadFailure', {
    culpritUrl: culprit.name,
    transferSize: culprit.transferSize,
    duration: culprit.duration,
    responseStatus: culprit.responseStatus,
  });
}

export function startChunkLoadRecovery() {
  window.addEventListener('vite:preloadError', (event) => {
    const module = event.payload?.message ?? 'unknown';
    // Lets us filter/alert on this failure class in Sentry independent of whatever
    // uncaught exception eventually gets thrown and grouped under its own message.
    Sentry.setTag('chunkPreloadError', true);
    attachCssPreloadDiagnostics(module);

    if (getIsPageTearingDown()) {
      recordSuppressedError(SuppressedErrorReason.ChunkLoadDuringTeardown, {
        module,
      });
      return;
    }

    // Without the cooldown a chunk that is permanently gone reloads forever.
    if (hasRecentlyReloaded()) {
      recordSuppressedError(SuppressedErrorReason.ChunkLoadReloadSuppressed, {
        module,
      });
      return;
    }

    recordSuppressedError(
      SuppressedErrorReason.ChunkLoadReload,
      { module },
      { flushImmediately: true },
    );

    event.preventDefault();
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()));
    window.location.reload();
  });
}
